/**
 * AllowlistTables — bootstrap (env, read-only) and runtime allowlist tables.
 */
import React from "react";
import { Trash2 } from "lucide-react";
import { fmtDate, SourcePill, TypePill } from "./helpers";

// Roles a founder can assign from the UI (founder itself is env-managed only).
// sales_manager / sales_person reach the admin dashboard (incl. Assessments);
// trial_user / campaign_user are time-limited tool-surface roles WITHOUT the
// admin menu.
export const ASSIGNABLE_ROLES = [
  { value: "sales_manager", label: "Sales manager — full pipeline + assessments" },
  { value: "sales_person", label: "Sales person — run assessments + own pipeline" },
  { value: "trial_user", label: "Trial user — time-limited, no admin menu" },
  { value: "campaign_user", label: "Campaign user — one-off, no admin menu" },
];

function RoleSelect({ entry, onSetRole }) {
  if (entry.type !== "email") {
    return <span className="text-zinc-400 text-[12px]">— domain —</span>;
  }
  const current = entry.platform_role || "";
  return (
    <select
      value={current}
      onChange={(e) => onSetRole(entry, e.target.value)}
      data-testid={`allowlist-role-${entry.id}`}
      className="text-[12px] border border-zinc-300 bg-white px-2 py-1 max-w-[16rem] focus:outline-none focus:ring-1 focus:ring-[#7737F6]"
    >
      {!current && <option value="" disabled>⚠ Unset → full access (founder)</option>}
      {ASSIGNABLE_ROLES.map((r) => (
        <option key={r.value} value={r.value}>{r.label}</option>
      ))}
    </select>
  );
}

export function BootstrapTable({ rows }) {
  return (
    <section className="mb-10" data-testid="users-bootstrap-section">
      <h3 className="text-[13px] font-semibold tracking-[0.16em] uppercase text-zinc-500 mb-3">
        Bootstrap allowlist <span className="text-zinc-400 normal-case tracking-normal">(from .env — read-only)</span>
      </h3>
      {rows.length === 0 ? (
        <p className="text-zinc-400 text-[13px]">None.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-zinc-200">
          <table className="min-w-full text-[13px]">
            <thead className="bg-zinc-50 text-left text-zinc-500 text-[11px] uppercase tracking-[0.12em]">
              <tr>
                <th className="p-3">Type</th>
                <th className="p-3">Value</th>
                <th className="p-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b, i) => (
                <tr key={`${b.type}-${b.value}-${i}`} className="border-t border-zinc-100">
                  <td className="p-3"><TypePill type={b.type} /></td>
                  <td className="p-3 font-mono text-zinc-800">{b.value}</td>
                  <td className="p-3"><SourcePill source="env" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function RuntimeAllowlistTable({ rows, onRemove, onSetRole }) {
  return (
    <section className="mb-10" data-testid="users-runtime-section">
      <h3 className="text-[13px] font-semibold tracking-[0.16em] uppercase text-zinc-500 mb-3">
        Runtime allowlist <span className="text-zinc-400 normal-case tracking-normal">({rows.length})</span>
      </h3>
      <p className="text-zinc-500 text-[12.5px] mb-3 max-w-3xl">
        The <strong>Role</strong> controls what each person can access. <strong>Sales manager</strong> and
        <strong> sales person</strong> can sign into this admin dashboard and run assessments;
        <strong> trial</strong> / <strong>campaign</strong> users only get the time-limited tool surface (no admin menu).
        An <em>unset</em> role grants full founder access — set a role to restrict it.
      </p>
      {rows.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-8 text-center">
          <p className="text-zinc-600 text-[14px]">No runtime entries yet. Use the form above to invite a colleague or trust a domain.</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-zinc-200">
          <table className="min-w-full text-[13px]">
            <thead className="bg-zinc-50 text-left text-zinc-500 text-[11px] uppercase tracking-[0.12em]">
              <tr>
                <th className="p-3">Type</th>
                <th className="p-3">Value</th>
                <th className="p-3">Role / access</th>
                <th className="p-3">State</th>
                <th className="p-3">Added</th>
                <th className="p-3">By</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`allowlist-row-${r.id}`}>
                  <td className="p-3"><TypePill type={r.type} /></td>
                  <td className="p-3 font-mono text-zinc-800">{r.value}</td>
                  <td className="p-3"><RoleSelect entry={r} onSetRole={onSetRole} /></td>
                  <td className="p-3"><SourcePill source={r.suspended ? "suspended" : "runtime"} /></td>
                  <td className="p-3 whitespace-nowrap text-zinc-600">{fmtDate(r.created_at)}</td>
                  <td className="p-3 text-zinc-600">{r.added_by || "—"}</td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      onClick={() => onRemove(r)}
                      className="text-[12px] text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                      data-testid={`allowlist-remove-${r.id}`}
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
